import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, ChevronLeft } from "lucide-react";
import { getFeaturedStates } from "@/lib/api";
import { T } from "@/context/LanguageContext";

export function SectionHeading({ kicker, title, action }) {
  return (
    <div className="mb-8 flex items-end justify-between gap-4">
      <div>
        {kicker && <div className="mb-1 text-xs font-bold uppercase tracking-[0.2em] text-oldglory-red"><T>{kicker}</T></div>}
        <h2 className="font-display text-3xl font-bold text-navy md:text-4xl">
          <T>{title}</T>
          <span className="mt-2 block h-[3px] w-16 bg-oldglory-red" />
        </h2>
      </div>
      {action}
    </div>
  );
}

export function ExploreByState() {
  const [states, setStates] = useState([]);
  const scroller = useRef(null);

  useEffect(() => {
    getFeaturedStates().then((d) => setStates(d.states)).catch(() => {});
  }, []);

  const scroll = (dir) => {
    scroller.current?.scrollBy({ left: dir * 340, behavior: "smooth" });
  };

  return (
    <section className="mx-auto max-w-[1500px] px-4 py-16">
      <SectionHeading
        kicker="Fifty States, Endless Stories"
        title="Explore by State"
        action={
          <Link to="/explore/states" className="hidden items-center gap-1.5 rounded-md border border-navy/20 px-4 py-2 text-sm font-semibold text-navy transition-colors hover:border-navy hover:bg-navy hover:text-white sm:inline-flex" data-testid="view-all-states">
            <T>View All States</T> <ChevronRight className="h-4 w-4" />
          </Link>
        }
      />

      <div className="relative">
        <button onClick={() => scroll(-1)} className="absolute -left-4 top-1/2 z-10 hidden -translate-y-1/2 rounded-full bg-white p-2 shadow-lg ring-1 ring-border transition-transform hover:scale-110 lg:block" data-testid="states-prev"><ChevronLeft className="h-5 w-5 text-navy" /></button>
        <button onClick={() => scroll(1)} className="absolute -right-4 top-1/2 z-10 hidden -translate-y-1/2 rounded-full bg-white p-2 shadow-lg ring-1 ring-border transition-transform hover:scale-110 lg:block" data-testid="states-next"><ChevronRight className="h-5 w-5 text-navy" /></button>

        <div ref={scroller} className="no-scrollbar flex snap-x gap-5 overflow-x-auto pb-2">
          {states.map((s, i) => (
            <motion.div
              key={s.geo_id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 6) * 0.06, duration: 0.5 }}
              className="w-[260px] shrink-0 snap-start"
            >
              <Link to={`/place${s.url_path}`} className="group block overflow-hidden rounded-xl bg-white shadow-md ring-1 ring-border transition-all hover:shadow-2xl hover:ring-navy/30" data-testid={`state-card-${s.geo_id}`}>
                <div className="relative h-44 overflow-hidden">
                  <img src={s.image_url} alt={s.official_name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent" />
                  <span className="absolute left-3 top-3 rounded bg-white/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-navy">{s.state_code}</span>
                </div>
                <div className="p-4">
                  <div className="font-display text-xl font-bold text-navy group-hover:text-oldglory-red transition-colors">{s.official_name}</div>
                  <div className="text-sm text-muted-foreground">{s.nickname}</div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
